/**
 * Row-action policy for the Holds table (util B).
 *
 * Encodes, in one place, what the Action cell offers for a given hold — the
 * dropdown options for actionable PENDING rows, or a read-only terminal label
 * for settled rows. PENDING rows past their expiry only ever offer Reverse
 * (Confirm would be rejected by the wallet API). Dropdown options are further
 * filtered by the operator's permissions.
 *
 * @param {{ status?: string, expires_at?: string, expired_at?: string, confirmed_at?: string, reversed_at?: string }} hold
 * @param {{ confirm?: boolean, reverse?: boolean }} perms - operator capabilities
 * @param {number|Date} [now] - current time (defaults to Date.now())
 * @returns {{ kind: string, actions?: string[], label?: string, at?: string }}
 *   kind ∈ 'dropdown_both' | 'dropdown_confirm_only' | 'dropdown_reverse_only'
 *        | 'terminal_label' | 'none'
 */
export function getRowAction(hold, perms = {}, now = Date.now()) {
  const status = (hold && hold.status ? String(hold.status) : "").toUpperCase();
  const nowMs = now instanceof Date ? now.getTime() : now;

  if (status === "CONFIRMED") {
    return { kind: "terminal_label", label: "Confirmed", at: hold.confirmed_at };
  }
  if (status === "REVERSED") {
    return { kind: "terminal_label", label: "Reversed", at: hold.reversed_at };
  }
  if (status === "EXPIRED") {
    return { kind: "terminal_label", label: "Expired", at: hold.expired_at };
  }

  if (status !== "PENDING") {
    // Unknown/unsupported state — render nothing actionable rather than guess.
    return { kind: "none" };
  }

  const isExpired = isPastExpiry(hold.expires_at, nowMs);
  const canConfirm = Boolean(perms.confirm) && !isExpired;
  const canReverse = Boolean(perms.reverse);

  const actions = [];
  if (canConfirm) actions.push("confirm");
  if (canReverse) actions.push("reverse");

  if (actions.length === 0) return { kind: "none" };
  if (actions.length === 2) return { kind: "dropdown_both", actions };
  return {
    kind: actions[0] === "confirm" ? "dropdown_confirm_only" : "dropdown_reverse_only",
    actions,
  };
}

/**
 * True when a PENDING hold's expiry has passed. Used both by the action policy
 * and by the table to render the `(expired)` suffix / highlight.
 * @param {string|undefined} expiresAt
 * @param {number|Date} [now]
 * @returns {boolean}
 */
export function isPastExpiry(expiresAt, now = Date.now()) {
  if (!expiresAt) return false;
  const expMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expMs)) return false;
  const nowMs = now instanceof Date ? now.getTime() : now;
  return expMs < nowMs;
}
